import { useParams } from 'react-router-dom';
import {
  makeStyles, shorthands, tokens, Text, Badge, MessageBar, MessageBarBody,
} from '@fluentui/react-components';
import { Page, PageHeader, Card, Loading, ErrorState, Empty, SeverityBadge } from '../components/common';
import { FindingList } from '../components/FindingList';
import { PILLARS, SEVERITY_ORDER } from '../theme/pillars';
import { useCustomer } from '../context/CustomerContext';
import type { MsPillar, Severity } from '../api/client';

const useStyles = makeStyles({
  summary: {
    display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
    columnGap: tokens.spacingHorizontalM, rowGap: tokens.spacingVerticalM,
  },
  stat: { display: 'flex', flexDirection: 'column', rowGap: '2px' },
  metric: { fontSize: tokens.fontSizeHero700, fontWeight: tokens.fontWeightSemibold, lineHeight: '1' },
  label: {
    color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200,
    textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: tokens.fontWeightSemibold,
  },
  coverage: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalXS },
  row: {
    display: 'flex', alignItems: 'center', columnGap: tokens.spacingHorizontalS,
    ...shorthands.padding(tokens.spacingVerticalXS, 0),
    ...shorthands.borderBottom('1px', 'solid', tokens.colorNeutralStroke3),
  },
  grow: { flexGrow: 1 },
  muted: { color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  section: { display: 'flex', flexDirection: 'column', rowGap: tokens.spacingVerticalS },
});

export function PillarPage() {
  const styles = useStyles();
  const { pillarId } = useParams<{ pillarId: string }>();
  const { tenant, run, primitives, findingsForPillar, loading, error } = useCustomer();

  const pillar = PILLARS.find((p) => p.id === pillarId);
  if (!pillar) return <ErrorState error={`Unknown pillar "${pillarId}".`} title="Not found" />;
  if (loading) return <Loading />;
  if (error) return <ErrorState error={error} />;

  if (!tenant) {
    return (
      <Page>
        <PageHeader title={pillar.label} description={pillar.description} />
        <Empty>Select a customer to see their {pillar.label.toLowerCase()} posture.</Empty>
      </Page>
    );
  }

  if (!run) {
    return (
      <Page>
        <PageHeader title={pillar.label} description={pillar.description} />
        <Empty>
          No collection has run for {tenant.displayName} yet, so there is nothing to show.
        </Empty>
      </Page>
    );
  }

  const findings = findingsForPillar(pillar.id as MsPillar);
  const inPillar = primitives.filter((p) => p.msPillar === pillar.id);

  const counts = SEVERITY_ORDER.reduce<Record<string, number>>((acc, s) => {
    acc[s] = findings.filter((f) => f.severity === s).length;
    return acc;
  }, {});

  const withFindings = new Set(
    findings.map((f) => f.primitiveId).filter((p): p is string => Boolean(p)),
  );
  const quiet = inPillar.filter((p) => !withFindings.has(p.primitiveId));
  const urgent = (counts['Critical'] ?? 0) + (counts['High'] ?? 0);

  return (
    <Page>
      <PageHeader
        title={pillar.label}
        description={`${pillar.description} ${tenant.displayName}, collection of ${new Date(run.startedUtc).toISOString().slice(0, 10)}.`}
      />

      {run.status === 'failed' && (
        <MessageBar intent="error">
          <MessageBarBody>
            This collection failed and is incomplete. Anything absent below may simply
            not have been collected.
          </MessageBarBody>
        </MessageBar>
      )}

      {/* 1. Posture at a glance */}
      <Card>
        <div className={styles.section}>
          <Text weight="semibold" size={400}>Posture</Text>
          <div className={styles.summary}>
            <div className={styles.stat}>
              <span className={styles.metric}>{findings.length}</span>
              <span className={styles.label}>Findings</span>
            </div>
            <div className={styles.stat}>
              <span className={styles.metric} style={{ color: urgent > 0 ? tokens.colorStatusDangerForeground1 : undefined }}>
                {urgent}
              </span>
              <span className={styles.label}>Critical or high</span>
            </div>
            {SEVERITY_ORDER.map((s) => (
              <div key={s} className={styles.stat}>
                <span className={styles.metric}>{counts[s] ?? 0}</span>
                <span className={styles.label}>{s}</span>
              </div>
            ))}
          </div>
        </div>
      </Card>

      {/* 2. Coverage: which outcomes were assessed and which raised nothing */}
      <Card>
        <div className={styles.section}>
          <Text weight="semibold" size={400}>Control coverage</Text>
          <Text className={styles.muted}>
            {withFindings.size} of {inPillar.length} control outcomes in this pillar raised
            a finding. The remainder raised nothing.
          </Text>

          {/* The distinction that matters in a compliance tool: no finding is
              not the same as compliant. It means nothing was raised, which
              could be a pass or a check that never ran. */}
          <MessageBar intent="info">
            <MessageBarBody>
              A control with no finding is not evidence of compliance. It means nothing
              was raised, which may be a pass or a check that did not run against this
              tenant. The collection coverage report distinguishes the two.
            </MessageBarBody>
          </MessageBar>

          <div className={styles.coverage}>
            {inPillar.map((p) => {
              const forThis = findings.filter((f) => f.primitiveId === p.primitiveId);
              const worst = SEVERITY_ORDER.find((s) => forThis.some((f) => f.severity === s));
              return (
                <div key={p.primitiveId} className={styles.row}>
                  <span className={styles.grow}>
                    <Text size={300}>{p.title}</Text>
                    <div className={styles.muted}>{p.primitiveId}</div>
                  </span>
                  {worst ? (
                    <>
                      <SeverityBadge severity={worst as Severity} />
                      <Badge appearance="tint" color="informative" size="small">
                        {forThis.length}
                      </Badge>
                    </>
                  ) : (
                    <Badge appearance="outline" size="small">Nothing raised</Badge>
                  )}
                </div>
              );
            })}
          </div>

          {quiet.length === inPillar.length && (
            <Text className={styles.muted}>
              No findings anywhere in this pillar for this collection.
            </Text>
          )}
        </div>
      </Card>

      {/* 3. The findings themselves */}
      <Card>
        <div className={styles.section}>
          <Text weight="semibold" size={400}>Findings</Text>
          <FindingList
            findings={findings}
            tenantId={tenant.tenantId}
            emptyMessage={`No ${pillar.label.toLowerCase()} findings were raised in this collection.`}
          />
        </div>
      </Card>
    </Page>
  );
}
